import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import InputText from "shared/components/inputs/InputText";
import InputMultiSelect from "shared/components/inputs/InputMultiSelect";
import Button from "shared/components/buttons/Button";
import fetchGraphQLErrors from "helpers/fetchGraphqlErrors";
import { snackbarError, snackbarSuccess } from "shared/redux/actions/Snackbar";
import fetchOrganisationLabelsAction from "redux/actions/fetchOrganisationLabelsAction";
import createOrganisationLabelAction from "redux/actions/createOrganisationLabelAction";
import setOrganisationLabelsAction from "redux/actions/setOrganisationLabelsAction";
import editOrganisationLabelAction from "redux/actions/editOrganisationLabelAction";
import removeOrganisationLabelAction from "redux/actions/removeOrganisationLabelAction";
import { fetchOrganisationAction } from "redux/actions/fetchOrganisationAction";
import { isValidIdentifier } from "helpers/isValidIdentifier";
import { setOrganisation } from "redux/actions";
import { message } from "constants/validation";
import Icon, { ICONS } from "shared/components/Icon";
import OkWarning from "shared/components/OkWarning";

import "./style.scss";

export default function TabLabels({ organisation, onClose }) {
    const [t] = useTranslation();
    const dispatch = useDispatch();

    const [availableLabels, setAvailableLabels] = useState(mapOrganisationCurrentLabels(organisation));
    const [selectedTagIds, setSelectedTagIds] = useState(
        mapOrganisationCurrentLabels(organisation).map(({ id }) => id)
    );
    const [newLabel, setNewLabel] = useState("");
    const [isCreatingLabel, setIsCreatingLabel] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingLabelId, setEditingLabelId] = useState(null);
    const [editingLabelValue, setEditingLabelValue] = useState("");
    const [busyLabelIds, setBusyLabelIds] = useState([]);
    const [pendingDeleteLabelId, setPendingDeleteLabelId] = useState(null);

    useEffect(() => {
        const currentLabels = mapOrganisationCurrentLabels(organisation);
        setSelectedTagIds(currentLabels.map(({ id }) => id));
        setAvailableLabels(labels => mergeLabels(labels, currentLabels));
    }, [organisation]);

    useEffect(() => {
        dispatch(fetchOrganisationLabelsAction())
            .then(result => {
                const currentLabels = result?.data?.adminCompanyLabels;
                if (Array.isArray(currentLabels)) {
                    const mappedLabels = currentLabels.map(({ id, name }) => ({ id, label: name }));
                    setAvailableLabels(labels => mergeLabels(labels, mappedLabels));
                }
            })
            .catch(error => {
                const errorMessage = fetchGraphQLErrors(error);
                dispatch(snackbarError(errorMessage));
            });
    }, [dispatch]);

    const labelItems = useMemo(() => {
        return availableLabels.map(tag => ({
            value: tag.id,
            label: tag.label
        }));
    }, [availableLabels]);

    const onCreateLabel = async () => {
        if (isCreatingLabel) {
            return;
        }

        const trimmedLabel = newLabel.trim();
        if (!trimmedLabel) {
            return;
        }

        const existingTag = availableLabels.find(tag =>
            tag?.label?.toLowerCase() === trimmedLabel.toLowerCase()
        );
        if (existingTag?.id) {
            setSelectedTagIds(currentIds => currentIds.includes(existingTag.id)
                ? currentIds
                : [...currentIds, existingTag.id]);
            setNewLabel("");
            return;
        }

        setIsCreatingLabel(true);

        try {
            const result = await dispatch(createOrganisationLabelAction(trimmedLabel));
            const createdTag = result?.data?.addCompanyLabel;

            if (createdTag && isValidIdentifier(createdTag.id)) {
                setAvailableLabels(currentLabels => mergeLabels(currentLabels, [createdTag]));
                setSelectedTagIds(currentIds => currentIds.includes(createdTag.id)
                    ? currentIds
                    : [...currentIds, createdTag.id]);
                setNewLabel("");
            }
        } catch (error) {
            const errorMessage = fetchGraphQLErrors(error);
            dispatch(snackbarError(errorMessage));
        } finally {
            setIsCreatingLabel(false);
        }
    };

    const onSave = async () => {
        if (isSaving) {
            return;
        }

        if (!isValidIdentifier(organisation?.id)) {
            return;
        }

        setIsSaving(true);

        try {
            await dispatch(setOrganisationLabelsAction(organisation.id, selectedTagIds));
            const organisationResult = await dispatch(fetchOrganisationAction(organisation.id));
            const refreshedOrganisation = organisationResult?.data?.adminOrganisation;
            if (refreshedOrganisation) {
                dispatch(setOrganisation(refreshedOrganisation));
            }
            dispatch(snackbarSuccess(t(message.organisationSaved)));
        } catch (error) {
            const errorMessage = fetchGraphQLErrors(error);
            dispatch(snackbarError(errorMessage));
        } finally {
            setIsSaving(false);
        }
    };

    const onStartEditLabel = (labelItem) => {
        setEditingLabelId(labelItem.id);
        setEditingLabelValue(labelItem.label);
    };

    const onCancelEditLabel = () => {
        setEditingLabelId(null);
        setEditingLabelValue("");
    };

    const onEditLabel = async () => {
        const labelId = editingLabelId;

        if (!isValidIdentifier(labelId)) {
            return;
        }

        const trimmedLabel = editingLabelValue.trim();
        if (!trimmedLabel) {
            return;
        }

        const existingLabel = availableLabels.find(label =>
            label.id !== labelId &&
            label?.label?.toLowerCase() === trimmedLabel.toLowerCase()
        );

        if (existingLabel) {
            dispatch(snackbarError("Label already exists"));
            return;
        }

        setBusyLabelIds(ids => [...ids, labelId]);
        try {
            const result = await dispatch(editOrganisationLabelAction(labelId, trimmedLabel));
            const editedLabel = result?.data?.editCompanyLabel;
            if (editedLabel && isValidIdentifier(editedLabel.id)) {
                setAvailableLabels(labels => labels.map(label =>
                    label.id === editedLabel.id ? editedLabel : label
                ));
            }
            onCancelEditLabel();
        } catch (error) {
            const errorMessage = fetchGraphQLErrors(error);
            dispatch(snackbarError(errorMessage));
        } finally {
            setBusyLabelIds(ids => ids.filter(id => id !== labelId));
        }
    };

    const onDeleteRequest = (id) => {
        if (!isValidIdentifier(id) || busyLabelIds.includes(id)) {
            return;
        }
        setPendingDeleteLabelId(id);
    };

    const onDeleteCancel = () => {
        setPendingDeleteLabelId(null);
    };

    const onDeleteConfirm = async () => {
        const id = pendingDeleteLabelId;
        if (!isValidIdentifier(id) || busyLabelIds.includes(id)) {
            return;
        }

        setBusyLabelIds(ids => [...ids, id]);
        try {
            await dispatch(removeOrganisationLabelAction(id));
            setAvailableLabels(labels => labels.filter(label => label.id !== id));
            setSelectedTagIds(ids => ids.filter(currentId => currentId !== id));
            if (editingLabelId === id) {
                onCancelEditLabel();
            }
            setPendingDeleteLabelId(null);
        } catch (error) {
            const errorMessage = fetchGraphQLErrors(error);
            dispatch(snackbarError(errorMessage));
        } finally {
            setBusyLabelIds(ids => ids.filter(currentId => currentId !== id));
        }
    };

    return (
        <div className="tab-labels">
            <h3>{t("labels.label")}</h3>
            <div className="spacer" />
            <div className="tab-content">
                <div className="cols">
                    <div className="col">
                        <InputMultiSelect
                            name="labels"
                            label="Select labels"
                            items={labelItems}
                            defaultValue={selectedTagIds}
                            onChange={(tagIds) => setSelectedTagIds(tagIds)}
                            error={false}
                        />
                    </div>
                </div>
                <div className="cols">
                    <div className="container-buttons col">
                        <Button
                            label={t("button.cancel.label")}
                            spacedLeft
                            outlined
                            onClick={onClose}
                        />
                        <Button
                            label={t("button.save.label")}
                            spacedLeft
                            onClick={onSave}
                            disabled={isSaving}
                        />
                    </div>
                </div>
                <div className="horizontal-divider-gray" />
                {/* <div className="spacer" /> */}
                <div className="cols tab-labels-add-row">
                    <div className="col">
                        <InputText
                            name="newLabel"
                            label="Add new label"
                            value={newLabel}
                            onChange={event => setNewLabel(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    onCreateLabel();
                                }
                            }}
                            noMarginBottom
                        />
                    </div>
                    <div className="col tab-labels-add-button">
                        <Button
                            label="Add"
                            onClick={onCreateLabel}
                            disabled={isCreatingLabel || !newLabel.trim()}
                        />
                    </div>
                </div>
                <div className="cols">
                    <div className="col">
                        <div className="tab-labels-list">
                            {availableLabels.map(labelItem => {
                                const isEditing = editingLabelId === labelItem.id;
                                const isBusy = busyLabelIds.includes(labelItem.id);
                                return (
                                    <div key={labelItem.id} className="tab-labels-list-item">
                                        <div className="tab-labels-list-item-content">
                                            {isEditing ?
                                                <InputText
                                                    name={`edit-label-${labelItem.id}`}
                                                    label="Label"
                                                    value={editingLabelValue}
                                                    onChange={event => setEditingLabelValue(event.target.value)}
                                                    noMarginBottom
                                                /> :
                                                <span>{labelItem.label}</span>
                                            }
                                        </div>
                                        <div className="tab-labels-list-item-actions">
                                            {isEditing ?
                                                <>
                                                    <Icon
                                                        name={ICONS.CHECK}
                                                        onClick={isBusy ? undefined : onEditLabel}
                                                        isInteractive={!isBusy}
                                                    />
                                                    <Icon
                                                        name={ICONS.CROSS}
                                                        onClick={isBusy ? undefined : onCancelEditLabel}
                                                        isInteractive={!isBusy}
                                                    />
                                                </> :
                                                <>
                                                    <Icon
                                                        name={ICONS.EDIT}
                                                        onClick={isBusy ? undefined :
                                                            () => onStartEditLabel(labelItem)}
                                                        isInteractive={!isBusy}
                                                    />
                                                    <Icon
                                                        name={ICONS.TRASH}
                                                        onClick={isBusy ? undefined :
                                                            () => onDeleteRequest(labelItem.id)}
                                                        isInteractive={!isBusy}
                                                    />
                                                </>
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="spacer-double" />
            </div>
            {pendingDeleteLabelId &&
                <OkWarning
                    onConfirm={onDeleteConfirm}
                    onCancel={onDeleteCancel}
                    title="Delete label"
                    message="Are you sure you want to delete this label?"
                />}
        </div>
    );
}

function mergeLabels(currentLabels, incomingLabels) {
    const result = new Map();
    [...currentLabels, ...incomingLabels].forEach(tag => {
        if (tag && isValidIdentifier(tag.id)) {
            result.set(tag.id, tag);
        }
    });
    return Array.from(result.values());
}

function mapOrganisationCurrentLabels(organisation) {
    return Array.isArray(organisation?.currentLabels) ?
        organisation.currentLabels.map(({ id, name }) => ({ id, label: name })) : [];
}

TabLabels.propTypes = {
    organisation: PropTypes.object,
    onClose: PropTypes.func.isRequired
};
